import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { UpgradeDialog } from "@/components/UpgradeDialog";

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const location = useLocation();
  const [sub, setSub] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const refresh = useCallback(async () => {
    const q = new URLSearchParams(location.search);
    const sessionId = q.get("checkout") === "success" ? q.get("session_id") : null;
    try {
      const { data } = sessionId
        ? await api.post("/subscription/confirm", { session_id: sessionId })
        : await api.get("/subscription");
      setSub(data);
    } catch (e) {
      setSub({ subscribed: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Any API call rejected by the paywall (HTTP 402) opens the upgrade dialog.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error.response?.status === 402) {
          setSub((s) => ({ ...(s || {}), subscribed: false }));
          setUpgradeOpen(true);
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const requirePaid = useCallback(
    (fn) => () => (sub?.subscribed ? fn() : setUpgradeOpen(true)),
    [sub]
  );

  return (
    <SubscriptionContext.Provider value={{ sub, refresh, requirePaid, openUpgrade: () => setUpgradeOpen(true) }}>
      {children}
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
